import { useCallback } from "react"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"

import { CodeGroupExtension } from "@productlane/lib/src/tiptap/extensions/code-group-extension"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { NodeViewProps } from "@tiptap/react"
import { PanelBar } from "./panel-bar"
import { usePanelGroup } from "./use-panel-group"

const codeLabel = (child: ProseMirrorNode, index: number): string => {
	const title = typeof child.attrs.title === "string" ? child.attrs.title : ""
	if (title.trim()) return title
	const language =
		typeof child.attrs.language === "string" ? child.attrs.language : ""
	if (language && language !== "plaintext") return language
	return `Code ${index + 1}`
}

function CodeGroupNodeView({ node, editor, getPos }: NodeViewProps) {
	const group = usePanelGroup({ node, editor, getPos, label: codeLabel })

	const onAdd = useCallback(() => {
		const pos = getPos()
		if (pos === undefined) return
		editor.commands.addCodeGroupBlock(pos)
	}, [editor, getPos])

	const onRemove = useCallback(
		(index: number) => {
			const pos = group.positionOf(index)
			if (pos === undefined) return
			editor.commands.removeCodeGroupBlock(pos)
		},
		[editor, group],
	)

	const onCopy = useCallback(
		() => node.child(group.active - 1)?.textContent ?? "",
		[node, group.active],
	)

	return (
		<NodeViewWrapper className="docs-panels" data-panels="code-group">
			<PanelBar
				{...group}
				addLabel="Add code block"
				removeLabel="Remove code block"
				onAdd={onAdd}
				onRemove={onRemove}
				onCopy={onCopy}
			/>
			<NodeViewContent
				data-panel-group="code-group"
				data-active={group.active}
			/>
		</NodeViewWrapper>
	)
}

export const CodeGroupWithNodeView = CodeGroupExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(CodeGroupNodeView)
	},
})
